import React, { useEffect, useMemo, useState } from "react";
import Nav from "../../master/NavBar";
import Header from "../../master/SideBar";
import { SweetAlertShow } from "../../master/Master";

import { getSessions } from "../../../api/SessionsService";
import {
  batchDetailsWithStudents,
  batchList,
  batchPromote,
} from "../../../api/Batch";

import { useLocation } from "react-router-dom";

export default function BatchPromote() {
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get("batchId");

  // ============================================================
  // STATE
  // ============================================================

  const [batch, setBatch] = useState(null);

  const [sessions, setSessions] = useState([]);
  const [sessionBatches, setSessionBatches] = useState([]);

  const [selectedSession, setSelectedSession] = useState("");
  const [selectedBatch, setSelectedBatch] = useState("");

  const [selectedStudents, setSelectedStudents] = useState([]);

  const [studentSearch, setStudentSearch] = useState("");

  const [batchLoading, setBatchLoading] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [transferLoading, setTransferLoading] = useState(false);

  // ============================================================
  // OLD BATCH DETAILS
  // ============================================================

  const getBatchInfo = async () => {
    if (!id) return;

    setBatchLoading(true);

    try {
      const response = await batchDetailsWithStudents(`id=${id}&status=5`);

      if (response?.data?.status) {
        setBatch(response?.data?.data || null);
      } else {
        setBatch(null);

        SweetAlertShow(
          "info",
          response?.data?.message || "Unable to load batch information!",
        );
      }
    } catch (error) {
      setBatch(null);

      SweetAlertShow(
        "error",
        error?.response?.data?.message ||
          error?.response?.message ||
          "Something went wrong while loading batch!",
      );
    } finally {
      setBatchLoading(false);
    }
  };

  // ============================================================
  // SESSIONS
  // ============================================================

  const fetchSessions = async () => {
    setSessionLoading(true);

    try {
      const response = await getSessions();

      if (response?.data?.status) {
        setSessions(response?.data?.data || []);
      } else {
        setSessions([]);

        SweetAlertShow(
          "info",
          response?.data?.message || "Unable to load sessions!",
        );
      }
    } catch (error) {
      setSessions([]);

      SweetAlertShow(
        "error",
        error?.response?.data?.message || "Unable to load sessions!",
      );
    } finally {
      setSessionLoading(false);
    }
  };

  // ============================================================
  // BATCHES BY SESSION
  // ============================================================

  const getSessionBatches = async (sessionId) => {
    if (!sessionId) {
      setSessionBatches([]);
      return;
    }

    setBatchLoading(true);

    try {
      const response = await batchList(`session_id=${sessionId}`);

      if (response?.data?.status) {
        setSessionBatches(response?.data?.data || []);
      } else {
        setSessionBatches([]);

        SweetAlertShow(
          "info",
          response?.data?.message || "Unable to load batches!",
        );
      }
    } catch (error) {
      setSessionBatches([]);

      SweetAlertShow(
        "error",
        error?.response?.data?.message || "Unable to load batches!",
      );
    } finally {
      setBatchLoading(false);
    }
  };

  // ============================================================
  // PROMOTE STUDENTS
  // ============================================================

  const promoteBatch = async (e) => {
    e.preventDefault();

    if (!selectedSession) {
      SweetAlertShow("warning", "Please select a new session!");
      return;
    }

    if (!selectedBatch) {
      SweetAlertShow("warning", "Please select a new batch!");
      return;
    }

    if (!selectedStudents.length) {
      SweetAlertShow("warning", "Please select at least one student!");
      return;
    }

    setTransferLoading(true);

    try {
      const payload = {
        old_batch_id: Number(id),
        new_batch_id: Number(selectedBatch),
        new_session_id: Number(selectedSession),
        student_ids: selectedStudents.map(Number),
      };

      const response = await batchPromote(payload);

      if (response?.data?.status) {
        SweetAlertShow(
          "success",
          response?.data?.message || "Students promoted successfully!",
        );

        setSelectedStudents([]);

        await getBatchInfo();
      } else {
        SweetAlertShow(
          "info",
          response?.data?.message || "Unable to promote students!",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        error?.response?.data?.message ||
          error?.response?.message ||
          "Something went wrong while promoting students!",
      );
    } finally {
      setTransferLoading(false);
    }
  };

  // ============================================================
  // EFFECTS
  // ============================================================

  useEffect(() => {
    fetchSessions();
  }, []);

  useEffect(() => {
    if (id) {
      getBatchInfo();
    }
  }, [id]);

  useEffect(() => {
    setSelectedBatch("");
    setSessionBatches([]);

    if (selectedSession) {
      getSessionBatches(selectedSession);
    }
  }, [selectedSession]);

  // ============================================================
  // TRANSFERABLE STUDENTS
  // ============================================================

  const transferableStudents = useMemo(() => {
    return (
      batch?.active_batch_students?.filter(
        (student) => !student?.next_batch_n_fee_id,
      ) || []
    );
  }, [batch]);

  // ============================================================
  // SEARCH STUDENTS
  // ============================================================

  const filteredStudents = useMemo(() => {
    const search = studentSearch.trim().toLowerCase();

    if (!search) {
      return transferableStudents;
    }

    return transferableStudents.filter((student) => {
      const rollNumber = String(student?.roll_number || "").toLowerCase();

      const name = String(student?.student_info?.name || "").toLowerCase();

      const fatherName = String(
        student?.student_info?.father_name || "",
      ).toLowerCase();

      const phone = String(student?.student_info?.phone || "").toLowerCase();

      return (
        rollNumber.includes(search) ||
        name.includes(search) ||
        fatherName.includes(search) ||
        phone.includes(search)
      );
    });
  }, [transferableStudents, studentSearch]);

  // ============================================================
  // SELECT ALL
  // ============================================================

  const allVisibleSelected =
    filteredStudents.length > 0 &&
    filteredStudents.every((student) =>
      selectedStudents.includes(Number(student?.student_id)),
    );

  const handleSelectAll = (checked) => {
    if (checked) {
      const visibleIds = filteredStudents.map((student) =>
        Number(student?.student_id),
      );

      setSelectedStudents((previous) =>
        Array.from(new Set([...previous, ...visibleIds])),
      );
    } else {
      const visibleIds = new Set(
        filteredStudents.map((student) => Number(student?.student_id)),
      );

      setSelectedStudents((previous) =>
        previous.filter((studentId) => !visibleIds.has(studentId)),
      );
    }
  };

  // ============================================================
  // SELECT INDIVIDUAL STUDENT
  // ============================================================

  const handleStudentSelect = (studentId, checked) => {
    const numericId = Number(studentId);

    if (checked) {
      setSelectedStudents((previous) => {
        if (previous.includes(numericId)) {
          return previous;
        }

        return [...previous, numericId];
      });
    } else {
      setSelectedStudents((previous) =>
        previous.filter((item) => item !== numericId),
      );
    }
  };

  // ============================================================
  // SELECTED BATCH
  // ============================================================

  const selectedBatchInfo = useMemo(() => {
    return sessionBatches.find(
      (item) => String(item?.id) === String(selectedBatch),
    );
  }, [sessionBatches, selectedBatch]);

  // ============================================================
  // UI
  // ============================================================

  return (
    <>
      {/* ========================================================
          PROMOTION LOADER
      ========================================================= */}

      {transferLoading && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            zIndex: 99999,
            background: "rgba(255,255,255,0.78)",
            backdropFilter: "blur(3px)",
          }}
        >
          <div className="card border-0 shadow">
            <div className="card-body px-5 py-4 text-center">
              <div
                className="spinner-border text-primary mb-3"
                role="status"
                style={{
                  width: "3rem",
                  height: "3rem",
                }}
              />

              <h6 className="mb-1">Promoting Students</h6>

              <small className="text-muted">
                Please wait while student records are updated...
              </small>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================
          MAIN LAYOUT
      ========================================================= */}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <div className="row">
                  <div className="col-md-12">
                    {/* ==================================================
                        PAGE HEADER
                    =================================================== */}

                    <div className="card border-0 shadow-sm mb-3">
                      <div className="card-body">
                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                          <div className="d-flex align-items-center gap-3">
                            <div
                              className="rounded-3 bg-primary d-flex align-items-center justify-content-center"
                              style={{
                                width: 52,
                                height: 52,
                              }}
                            >
                              <i
                                className="ri-arrow-left-right-line text-white"
                                style={{
                                  fontSize: 28,
                                }}
                              />
                            </div>

                            <div>
                              <h4 className="mb-1">Student Batch Promotion</h4>

                              <p className="mb-0 text-muted">
                                Promote students from the current batch to a new
                                session and batch.
                              </p>
                            </div>
                          </div>

                          <div className="d-flex gap-2">
                            <span className="badge bg-label-primary px-3 py-2">
                              <i className="ri-group-line me-1" />
                              {transferableStudents.length} Available
                            </span>

                            <span className="badge bg-label-success px-3 py-2">
                              <i className="ri-checkbox-circle-line me-1" />
                              {selectedStudents.length} Selected
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ==================================================
                        BATCH PROMOTION FLOW
                    =================================================== */}

                    <div className="card border-0 shadow-sm mb-3">
                      <div className="card-body">
                        <div className="row g-3 align-items-stretch">
                          {/* CURRENT BATCH */}

                          <div className="col-lg-5">
                            <div className="border rounded-3 h-100 p-3">
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <div className="avatar avatar-sm">
                                  <span className="avatar-initial rounded bg-label-danger">
                                    <i className="ri-logout-box-r-line" />
                                  </span>
                                </div>

                                <div>
                                  <h6 className="mb-0">Current Batch</h6>

                                  <small className="text-muted">
                                    Students will be promoted from here
                                  </small>
                                </div>
                              </div>

                              {batchLoading && !batch ? (
                                <BatchInfoSkeleton />
                              ) : batch ? (
                                <div>
                                  <h5 className="mb-3">
                                    {batch?.title || "-"}
                                  </h5>

                                  <div className="row g-3">
                                    <InfoItem
                                      icon="ri-book-open-line"
                                      label="Course"
                                      value={batch?.course?.title || "-"}
                                    />

                                    <InfoItem
                                      icon="ri-calendar-line"
                                      label="Session"
                                      value={batch?.sessions?.session || "-"}
                                    />

                                    <InfoItem
                                      icon="ri-user-line"
                                      label="Teacher"
                                      value={
                                        batch?.class_teacher_info?.name || "-"
                                      }
                                    />

                                    <InfoItem
                                      icon="ri-time-line"
                                      label="Time"
                                      value={
                                        batch?.start_time && batch?.end_time
                                          ? `${batch.start_time} - ${batch.end_time}`
                                          : "-"
                                      }
                                    />

                                    <InfoItem
                                      icon="ri-calendar-event-line"
                                      label="Start Date"
                                      value={batch?.starting_date || "-"}
                                    />

                                    <InfoItem
                                      icon="ri-group-line"
                                      label="Students"
                                      value={transferableStudents.length}
                                    />
                                  </div>
                                </div>
                              ) : (
                                <EmptyState
                                  icon="ri-archive-line"
                                  title="Batch not found"
                                  description="Unable to load current batch information."
                                />
                              )}
                            </div>
                          </div>

                          {/* TRANSFER ARROW */}

                          <div className="col-lg-2 d-flex align-items-center justify-content-center">
                            <div className="text-center">
                              <div
                                className="rounded-circle bg-primary d-flex align-items-center justify-content-center mx-auto mb-2"
                                style={{
                                  width: 50,
                                  height: 50,
                                }}
                              >
                                <i
                                  className="ri-arrow-right-line text-white"
                                  style={{
                                    fontSize: 26,
                                  }}
                                />
                              </div>

                              <small className="text-muted">Promote</small>
                            </div>
                          </div>

                          {/* DESTINATION BATCH */}

                          <div className="col-lg-5">
                            <div className="border rounded-3 h-100 p-3">
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <div className="avatar avatar-sm">
                                  <span className="avatar-initial rounded bg-label-success">
                                    <i className="ri-login-box-line" />
                                  </span>
                                </div>

                                <div>
                                  <h6 className="mb-0">Destination Batch</h6>

                                  <small className="text-muted">
                                    Select where students should go
                                  </small>
                                </div>
                              </div>

                              {/* SESSION */}

                              <div className="mb-3">
                                <label className="form-label fw-semibold">
                                  <i className="ri-calendar-line me-1" />
                                  New Session
                                </label>

                                <div className="input-group">
                                  <span className="input-group-text">
                                    <i className="ri-calendar-line" />
                                  </span>

                                  <select
                                    className="form-select"
                                    value={selectedSession}
                                    onChange={(e) =>
                                      setSelectedSession(e.target.value)
                                    }
                                    disabled={sessionLoading || transferLoading}
                                  >
                                    <option value="">
                                      {sessionLoading
                                        ? "Loading sessions..."
                                        : "Select Session"}
                                    </option>

                                    {sessions
                                      ?.filter((item) => item?.status !== "2")
                                      .map((item) => (
                                        <option key={item?.id} value={item?.id}>
                                          {item?.session} (
                                          {item?.status === "0"
                                            ? "Upcoming"
                                            : "Active"}
                                          )
                                        </option>
                                      ))}
                                  </select>
                                </div>
                              </div>

                              {/* BATCH */}

                              <div className="mb-3">
                                <label className="form-label fw-semibold">
                                  <i className="ri-stack-line me-1" />
                                  New Batch
                                </label>

                                <div className="input-group">
                                  <span className="input-group-text">
                                    <i className="ri-stack-line" />
                                  </span>

                                  <select
                                    className="form-select"
                                    value={selectedBatch}
                                    onChange={(e) =>
                                      setSelectedBatch(e.target.value)
                                    }
                                    disabled={
                                      !selectedSession ||
                                      batchLoading ||
                                      transferLoading
                                    }
                                  >
                                    <option value="">
                                      {!selectedSession
                                        ? "Select session first"
                                        : batchLoading
                                          ? "Loading batches..."
                                          : "Select New Batch"}
                                    </option>

                                    {sessionBatches
                                      ?.filter((item) => item?.status !== "2")
                                      .map((item) => (
                                        <option key={item?.id} value={item?.id}>
                                          {item?.title} -{" "}
                                          {item?.course?.title || "No Course"} (
                                          {item?.status === "0"
                                            ? "Upcoming"
                                            : "Active"}
                                          )
                                        </option>
                                      ))}
                                  </select>
                                </div>
                              </div>

                              {/* SELECTED BATCH */}

                              {selectedBatchInfo && (
                                <div className="alert alert-success mb-0">
                                  <div className="d-flex gap-2">
                                    <i className="ri-checkbox-circle-line fs-4" />

                                    <div>
                                      <div className="fw-semibold">
                                        Destination Selected
                                      </div>

                                      <small>
                                        {selectedBatchInfo?.title}
                                        {" • "}
                                        {selectedBatchInfo?.course?.title ||
                                          "-"}
                                      </small>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* NO SESSION */}

                              {!selectedSession && (
                                <div className="text-center py-3 text-muted">
                                  <i
                                    className="ri-calendar-line"
                                    style={{
                                      fontSize: 28,
                                    }}
                                  />

                                  <div className="small mt-1">
                                    Select a session to see available batches.
                                  </div>
                                </div>
                              )}

                              {/* NO BATCH */}

                              {selectedSession &&
                                !batchLoading &&
                                sessionBatches.length === 0 && (
                                  <div className="text-center py-3 text-muted">
                                    <i
                                      className="ri-archive-line"
                                      style={{
                                        fontSize: 28,
                                      }}
                                    />

                                    <div className="small mt-1">
                                      No active or upcoming batch found.
                                    </div>
                                  </div>
                                )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ==================================================
                        STUDENTS
                    =================================================== */}

                    <div className="card border-0 shadow-sm">
                      <div className="card-body p-0">
                        {/* HEADER */}

                        <div className="p-3 border-bottom">
                          <div className="row align-items-center g-3">
                            <div className="col-md-6">
                              <div className="d-flex align-items-center gap-2">
                                <div
                                  className="rounded bg-label-primary d-flex align-items-center justify-content-center"
                                  style={{
                                    width: 40,
                                    height: 40,
                                  }}
                                >
                                  <i
                                    className="ri-group-line"
                                    style={{
                                      fontSize: 22,
                                    }}
                                  />
                                </div>

                                <div>
                                  <h5 className="mb-0">Select Students</h5>

                                  <small className="text-muted">
                                    Choose students to promote
                                  </small>
                                </div>
                              </div>
                            </div>

                            <div className="col-md-6">
                              <div className="d-flex justify-content-md-end">
                                <div
                                  className="input-group"
                                  style={{
                                    maxWidth: 300,
                                  }}
                                >
                                  <span className="input-group-text">
                                    <i className="ri-search-line" />
                                  </span>

                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search student..."
                                    value={studentSearch}
                                    onChange={(e) =>
                                      setStudentSearch(e.target.value)
                                    }
                                  />

                                  {studentSearch && (
                                    <button
                                      type="button"
                                      className="btn btn-outline-secondary"
                                      onClick={() => setStudentSearch("")}
                                    >
                                      <i className="ri-close-line" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* SUMMARY */}

                        <div className="px-3 py-2 bg-light border-bottom">
                          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                            <div className="d-flex gap-3">
                              <span className="text-muted small">
                                <i className="ri-group-line me-1" />
                                Available:{" "}
                                <strong>{transferableStudents.length}</strong>
                              </span>

                              <span className="text-primary small">
                                <i className="ri-checkbox-multiple-line me-1" />
                                Selected:{" "}
                                <strong>{selectedStudents.length}</strong>
                              </span>
                            </div>

                            {studentSearch && (
                              <span className="text-muted small">
                                Showing{" "}
                                <strong>{filteredStudents.length}</strong>{" "}
                                result(s)
                              </span>
                            )}
                          </div>
                        </div>

                        {/* STUDENT LOADING */}

                        {batchLoading && !batch ? (
                          <StudentTableSkeleton />
                        ) : transferableStudents.length === 0 ? (
                          <EmptyState
                            icon="ri-user-follow-line"
                            title="No students available"
                            description={
                              batch
                                ? "All students in this batch have already been promoted."
                                : "Student information is not available."
                            }
                          />
                        ) : filteredStudents.length === 0 ? (
                          <EmptyState
                            icon="ri-search-eye-line"
                            title="No students found"
                            description="Try another name, roll number or phone number."
                            action={
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => setStudentSearch("")}
                              >
                                <i className="ri-refresh-line me-1" />
                                Clear Search
                              </button>
                            }
                          />
                        ) : (
                          <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                              <thead>
                                <tr>
                                  <th
                                    className="text-center"
                                    style={{
                                      width: 70,
                                    }}
                                  >
                                    <div className="form-check d-flex justify-content-center">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={allVisibleSelected}
                                        onChange={(e) =>
                                          handleSelectAll(e.target.checked)
                                        }
                                        disabled={transferLoading}
                                      />
                                    </div>
                                  </th>

                                  <th>Student</th>

                                  <th>Roll No</th>

                                  <th>Student Reg No</th>

                                  <th>Father</th>

                                  <th>Phone</th>

                                  <th className="text-center">Status</th>
                                </tr>
                              </thead>

                              <tbody>
                                {filteredStudents.map((student, index) => {
                                  const studentId = Number(student?.student_id);

                                  const isSelected =
                                    selectedStudents.includes(studentId);

                                  const studentName =
                                    student?.student_info?.name ||
                                    "Unknown Student";

                                  const firstLetter = studentName
                                    .charAt(0)
                                    .toUpperCase();

                                  return (
                                    <tr
                                      key={student?.id || studentId || index}
                                      className={
                                        isSelected ? "table-primary" : ""
                                      }
                                    >
                                      {/* CHECKBOX */}

                                      <td className="text-center">
                                        <div className="form-check d-flex justify-content-center">
                                          <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={(e) =>
                                              handleStudentSelect(
                                                studentId,
                                                e.target.checked,
                                              )
                                            }
                                            disabled={transferLoading}
                                          />
                                        </div>
                                      </td>

                                      {/* STUDENT */}

                                      <td>
                                        
                                            <div className="fw-semibold">
                                              {studentName}
                                            </div>
                                      </td>

                                      {/* ROLL */}

                                      <td>
                                        <span className="fw-semibold">
                                          <i className="ri-id-card-line me-1 text-muted" />

                                          {student?.roll_number || "-"}
                                        </span>
                                      </td>

                                      {/* STUDENT REG NO */}

                                      <td>
                                        <span className="fw-semibold">
                                          <i className="ri-profile-line me-1 text-muted" />

                                          {student?.student_info
                                            ?.student_reg_no ||
                                            student?.student_info
                                              ?.registration_no ||
                                            student?.student_reg_no ||
                                            student?.registration_no ||
                                            "-"}
                                        </span>
                                      </td>

                                      {/* FATHER */}

                                      <td>
                                        <span>
                                          <i className="ri-user-line me-1 text-muted" />

                                          {student?.student_info?.father_name ||
                                            "-"}
                                        </span>
                                      </td>

                                      {/* PHONE */}

                                      <td>
                                        {student?.student_info?.phone ? (
                                          <a
                                            href={`tel:${student.student_info.phone}`}
                                            className="text-decoration-none"
                                          >
                                            <i className="ri-phone-line me-1" />

                                            {student.student_info.phone}
                                          </a>
                                        ) : (
                                          "-"
                                        )}
                                      </td>

                                      {/* STATUS */}

                                      <td className="text-center">
                                        {isSelected ? (
                                          <span className="badge bg-success">
                                            <i className="ri-check-line me-1" />
                                            Selected
                                          </span>
                                        ) : (
                                          <span className="badge bg-label-secondary">
                                            Available
                                          </span>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* ==================================================
                        PROMOTE ACTION
                    =================================================== */}

                    <div className="card border-0 shadow-sm mt-3">
                      <div className="card-body">
                        <div className="row align-items-center g-3">
                          <div className="col-md-7">
                            <div className="d-flex align-items-center gap-3">
                              <div
                                className="rounded-circle bg-label-primary d-flex align-items-center justify-content-center"
                                style={{
                                  width: 46,
                                  height: 46,
                                }}
                              >
                                <i
                                  className="ri-arrow-left-right-line"
                                  style={{
                                    fontSize: 22,
                                  }}
                                />
                              </div>

                              <div>
                                <h6 className="mb-1">Ready to Promote</h6>

                                <small className="text-muted">
                                  {selectedStudents.length > 0
                                    ? `${selectedStudents.length} student(s) selected for promotion`
                                    : "Select students and destination batch first."}
                                </small>
                              </div>
                            </div>
                          </div>

                          <div className="col-md-5 text-md-end">
                            <button
                              type="button"
                              className="btn btn-primary px-4"
                              disabled={
                                transferLoading ||
                                !selectedStudents.length ||
                                !selectedSession ||
                                !selectedBatch
                              }
                              onClick={promoteBatch}
                            >
                              {transferLoading ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                  />
                                  Promoting...
                                </>
                              ) : (
                                <>
                                  <i className="ri-arrow-left-right-line me-2" />
                                  Promote{" "}
                                  {selectedStudents.length > 0
                                    ? `${selectedStudents.length} `
                                    : ""}
                                  Student
                                  {selectedStudents.length !== 1 ? "s" : ""}
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ================================================================
// INFO ITEM
// ================================================================

const InfoItem = ({ icon, label, value }) => {
  return (
    <div className="col-12 col-sm-6">
      <div className="d-flex align-items-start gap-2">
        <i
          className={`${icon} text-primary mt-1`}
          style={{
            fontSize: 17,
          }}
        />

        <div>
          <small className="text-muted d-block">{label}</small>

          <span className="fw-semibold">{value}</span>
        </div>
      </div>
    </div>
  );
};

// ================================================================
// EMPTY STATE
// ================================================================

const EmptyState = ({
  icon = "ri-archive-line",
  title,
  description,
  action,
}) => {
  return (
    <div
      className="d-flex flex-column align-items-center justify-content-center text-center px-3"
      style={{
        minHeight: 260,
      }}
    >
      <div
        className="rounded-circle bg-label-primary d-flex align-items-center justify-content-center mb-3"
        style={{
          width: 68,
          height: 68,
        }}
      >
        <i
          className={icon}
          style={{
            fontSize: 32,
          }}
        />
      </div>

      <h6 className="mb-1">{title}</h6>

      <p
        className="text-muted mb-3"
        style={{
          maxWidth: 420,
        }}
      >
        {description}
      </p>

      {action}
    </div>
  );
};

// ================================================================
// BATCH SKELETON
// ================================================================

const BatchInfoSkeleton = () => {
  return (
    <div>
      <div
        className="placeholder-glow mb-3"
        style={{
          width: "70%",
        }}
      >
        <span className="placeholder col-12 rounded" />
      </div>

      <div className="row g-3">
        {[1, 2, 3, 4, 5, 6].map((item) => (
          <div className="col-12 col-sm-6" key={item}>
            <div className="placeholder-glow">
              <span className="placeholder col-4 me-2 rounded" />

              <span className="placeholder col-7 rounded" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ================================================================
// STUDENT TABLE SKELETON
// ================================================================

const StudentTableSkeleton = () => {
  return (
    <div className="table-responsive">
      <table className="table align-middle mb-0">
        <thead>
          <tr>
            <th style={{ width: 70 }}>
              <span className="placeholder col-4" />
            </th>

            <th>Student</th>

            <th>Roll No</th>

            <th>Student Reg No</th>

            <th>Father</th>

            <th>Phone</th>

            <th>Status</th>
          </tr>
        </thead>

        <tbody>
          {[1, 2, 3, 4, 5].map((item) => (
            <tr key={item}>
              <td className="text-center">
                <span className="placeholder col-4" />
              </td>

              <td>
                <div className="d-flex align-items-center gap-2">
                  <span
                    className="placeholder rounded-circle"
                    style={{
                      width: 36,
                      height: 36,
                    }}
                  />

                  <div className="placeholder-glow w-50">
                    <span className="placeholder col-12 rounded" />

                    <span className="placeholder col-7 rounded mt-1" />
                  </div>
                </div>
              </td>

              <td>
                <span className="placeholder col-7" />
              </td>

              <td>
                <span className="placeholder col-8" />
              </td>

              <td>
                <span className="placeholder col-8" />
              </td>

              <td>
                <span className="placeholder col-8" />
              </td>

              <td>
                <span className="placeholder col-8" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
